import api from './api';

export type SocialMedia = 'LinkedIn' | 'Facebook' | 'Twitter' | 'Instagram';

export interface TriggerEnrichmentPayload {
  organizationId: string;
  profileUrl: string;
  socialMedia: SocialMedia;
  name?: string;
  leadId?: string;
  /** Create a new lead even when one shares the company name — the user chose it. */
  forceNewLead?: boolean;
}

export interface EnrichmentTriggerResponse {
  jobId: string;
  message: string;
}

export interface RefreshPostsPayload {
  leadId: string;
  organizationId: string;
}

export interface RefreshPostsResponse {
  status: 'updated' | 'no_changes';
  postsLastRefreshedAt: string;
}

export interface WorkflowExecutionRecord {
  id: string;
  organizationId: string;
  workflowType: string;
  status: 'running' | 'success' | 'partial_success' | 'failed';
  leadsCreated: number;
  leadsUpdated: number;
  errorCount: number;
  errorMessage: string | null;
  durationMs: number | null;
  metadata: Record<string, unknown>;
  startedAt: string;
  completedAt: string | null;
}

export interface WorkflowExecutionsResponse {
  executions: WorkflowExecutionRecord[];
  stats: {
    totalByType: Record<string, { count: number; lastRun: string | null }>;
  };
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/** FLOSEAI-635 — Instagram discovery by hashtag, keyword or place. */
export interface InstagramDiscoveryPayload {
  organizationId: string;
  hashtags?: string[];
  keyword?: string;
  place?: string;
  maxResults?: number;
}

/**
 * Free, evidence-based signal that a candidate is actually local to the
 * searched place — a fenced post it authored, one of its posts tagged at the
 * place, or its own profile naming the place (FLOSEAI-635).
 */
export type InstagramLocalityEvidence =
  | 'posted_at_place'
  | 'post_tagged_at_place'
  | 'profile_mentions_place';

export interface InstagramDiscoveryCandidate {
  username: string;
  profileUrl: string;
  fullName?: string;
  followersCount?: number;
  isVerified?: boolean;
  isBusinessAccount?: boolean;
  businessCategory?: string;
  discoveredVia: string;
  matchCount: number;
  matchedPostUrls?: string[];
  localityEvidence?: InstagramLocalityEvidence[];
}

export interface InstagramDiscoveryResponse {
  candidates: InstagramDiscoveryCandidate[];
  /** Accounts found but already in the pipeline, so nothing was duplicated. */
  alreadyKnown: number;
  /**
   * How many keyword/hashtag candidates were found but dropped for lacking
   * locality evidence, once place narrowed the search. Zero outside combined
   * mode (FLOSEAI-635).
   */
  droppedForLocality: number;
  /**
   * How many place-only candidates were hidden as likely one-time visitors.
   * Zero outside place-only mode (FLOSEAI-635).
   */
  hiddenOneTimePosters: number;
  /**
   * True only in combined mode, when the place actor run itself failed —
   * narrowing then fell back to name-token evidence only (FLOSEAI-635).
   */
  placeSearchFailed?: boolean;
  /**
   * Which hashtag/keyword runs failed outright rather than resolving to
   * nothing. Kept even when other candidates were found and returned, so the
   * UI can warn that some results may be missing (FLOSEAI-635).
   */
  failedSearches?: ('hashtag' | 'keyword')[];
  /** Set when the run produced nothing, so the UI can say why. */
  emptyReason?:
    | 'no_criteria'
    | 'no_results'
    | 'all_already_known'
    | 'place_not_found'
    | 'place_search_failed'
    | 'no_local_match'
    | 'search_failed';
  estimate: {
    maxResults: number;
    estimatedCostUsd: number;
    ratePer1k: number;
    /** True when the plan, not the product ceiling, decided `maxResults`. */
    limitedByPlan: boolean;
  };
}

export interface AttachCandidate {
  leadId: string;
  fullName: string | null;
  company: string | null;
  /** Networks this lead already covers. */
  platforms: string[];
  /** True when this lead already holds the exact profile being prospected. */
  alreadyEnriched: boolean;
}

export const workflowsAPI = {
  /**
   * Existing leads this profile could belong to, asked before anything is
   * scraped. Prospecting used to decide alone, after the scrape, by comparing the
   * scraped company name.
   */
  attachCandidates: (organizationId: string, profileUrl: string, name?: string) =>
    api.get<AttachCandidate[]>('/lead-enrichment/attach-candidates', {
      params: { organizationId, profileUrl, ...(name ? { name } : {}) },
    }),

  triggerEnrichment: (payload: TriggerEnrichmentPayload) =>
    api.post<EnrichmentTriggerResponse>('/lead-enrichment/trigger', payload),

  /**
   * Discovery is a read: it spends Apify credit but creates no leads. Runs can
   * take a while, so it carries an extended timeout — LONGER than the
   * server's own ceiling.
   *
   * A `place` search used to be TWO sequential Apify actor runs; the second,
   * location-scraping run was deleted (FLOSEAI-635 QA reopen of #1557) — it
   * returned the same posts the place search already carried, so it only
   * doubled the cost and forced two `apiBreaker.fire` calls. A place search is
   * now the SAME single search-actor run as hashtag/keyword discovery, with
   * its own 120s `maxWaitMs` (apify-scraper.service.ts) inside ONE
   * `apiBreaker.fire` call, so the server's real ceiling is one 150000ms
   * breaker timeout again.
   *
   * This client timeout MUST stay above that single ceiling, or the client
   * aborts and reports failure while an actor run it already paid for is
   * still finishing server-side. Do not shrink this further without
   * re-checking the breaker timeout it is sized against.
   *
   * Discovery still runs synchronously behind this one request; moving it to
   * an asynchronous job (tracked separately, #1655) would remove the need for
   * a timeout this long in the first place.
   */
  discoverInstagramLeads: (payload: InstagramDiscoveryPayload) =>
    api.post<InstagramDiscoveryResponse>('/lead-enrichment/instagram/discover', payload, {
      timeout: 165000,
    }),

  estimateInstagramDiscovery: ({ hashtags, ...rest }: InstagramDiscoveryPayload) =>
    api.get<InstagramDiscoveryResponse['estimate']>(
      '/lead-enrichment/instagram/discover/estimate',
      { params: { ...rest, ...(hashtags?.length && { hashtags: hashtags.join(',') }) } },
    ),

  importInstagramCandidates: (payload: {
    organizationId: string;
    usernames: string[];
    segmentName: string;
  }) => api.post<{ enqueued: number }>('/lead-enrichment/instagram/import', payload),

  refreshPosts: (payload: RefreshPostsPayload) =>
    api.post<RefreshPostsResponse>('/lead-enrichment/refresh-posts', payload, {
      timeout: 130000,
    }),

  findLeadBySourceUrl: (organizationId: string, sourceUrl: string) =>
    api.get<{ leadId: string | null; assignedToOther?: boolean }>(
      '/lead-enrichment/find-by-source-url',
      {
        params: { organizationId, sourceUrl },
      },
    ),

  getJobStatus: (jobId: string, organizationId: string) =>
    api.get<{ state: string; result?: Record<string, unknown>; error?: string }>(
      '/lead-enrichment/job-status',
      { params: { jobId, organizationId } },
    ),

  getExecutions: (organizationId: string, limit = 20, offset = 0) =>
    api.get<WorkflowExecutionsResponse>('/workflow-executions', {
      params: { organizationId, limit, offset },
    }),

  triggerWebSearch: (payload: TriggerWebSearchPayload) =>
    api.post<WebSearchTriggerResponse>('/web-search/trigger', payload),

  confirmWebSearch: (payload: ConfirmWebSearchApiPayload) =>
    api.post<{ message: string }>('/web-search/confirm', payload),

  cancelWebSearch: (leadId: string, organizationId: string) =>
    api.post<{ message: string }>('/web-search/cancel', undefined, {
      params: { leadId, organizationId },
    }),

  getWebSearchResults: (leadId: string, organizationId: string) =>
    api.get<WebSearchResultsResponse>(`/web-search/results/${leadId}`, {
      params: { organizationId },
    }),
};

export interface TriggerWebSearchPayload {
  organizationId: string;
  leadId: string;
  maxLinks?: number;
  maxTimeMs?: number;
  maxResults?: number;
}

export interface WebSearchTriggerResponse {
  message: string;
}

export interface ConfirmWebSearchApiPayload {
  organizationId: string;
  leadId: string;
  contacts: Array<{ type: 'email' | 'phone'; value: string; sourceUrl: string }>;
  force?: boolean;
}

export interface WebSearchResultContact {
  type: 'email' | 'phone';
  value: string;
  sourceUrl: string;
  sourceType: string;
  confidence: string;
  context?: string;
}

export interface WebSearchResultsResponse {
  results: {
    leadId: string;
    contacts: WebSearchResultContact[];
    totalLinksExplored: number;
    searchQuery: string;
  } | null;
}
